import React, { useEffect, useMemo, useState } from "react";
import DashboardHeader from "../components/DashboardHeader";
import { socialApi } from "../lib/socialApi";
import "../style/onboarding2.css";


const PROVIDERS = [
  { id: "facebook", label: "Facebook", description: "Share to Facebook (opens sharer)" },
  { id: "linkedin", label: "LinkedIn", description: "Share to LinkedIn (opens composer)" },
];

// ---- helpers ----
function stripHtml(html = "") {
  return String(html)
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<\/?[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function truncate(text = "", n = 240) {
  const t = String(text);
  if (t.length <= n) return t;
  return t.slice(0, n - 1).trim() + "…";
}

export default function SocialConnect() {
  const [connected, setConnected] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  // post payload coming from PlannerPostModal via sessionStorage
  const [postContent, setPostContent] = useState({
    title: "",
    content: "",
    imageUrl: "",
    postId: "",
  });

  // load connected providers (your mock connect)
  useEffect(() => {
    (async () => {
      try {
        const accounts = await socialApi.getProviders();
        const map = {};
        accounts.forEach((acc) => (map[acc.provider] = acc));
        setConnected(map);
      } catch (err) {
        console.error(err);
      }
    })();
  }, []);

  // load share payload (fixes HTTP 431 because we do NOT use huge query params)
  useEffect(() => {
    const raw = sessionStorage.getItem("clikkle_share_payload");
    if (!raw) return;

    try {
      const parsed = JSON.parse(raw);
      setPostContent({
        title: parsed?.title || "",
        content: parsed?.content || "",
        imageUrl: parsed?.imageUrl || "",
        postId: parsed?.postId || "",
      });
    } catch (e) {
      console.error("Invalid clikkle_share_payload JSON", e);
    }
  }, []);

  const shareText = useMemo(() => {
    const clean = stripHtml(postContent.content || "");
    const body = clean; // keep it reasonable
    const title = (postContent.title || "").trim();
    if (title && body) return `${title}\n\n${body}`;
    return title || body || "";
  }, [postContent.title, postContent.content]);

  const handleConnect = async (providerId) => {
    try {
      setLoading(true);
      setMessage("");
      const acc = await socialApi.mockConnect(providerId);
      setConnected((prev) => ({ ...prev, [providerId]: acc }));
      setMessage(`${providerId} connected (mock). You can replace this with real OAuth later.`);
    } catch (err) {
      console.error(err);
      setMessage("Failed to connect. Check console.");
    } finally {
      setLoading(false);
    }
  };

  // IMPORTANT:
  // - LinkedIn preview/image cannot be forced unless you share a PUBLIC URL with OG tags.
  // - But we CAN prefill the post text using /feed/?shareActive=true&text=
  const shareToLinkedIn = () => {
    if (!shareText) {
      setMessage("Nothing to share. Open a post in the modal first.");
      return;
    }
    const url = `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(shareText)}`;
    window.open(url, "_blank", "width=980,height=720");
  };

  // Facebook sharer:
  // - You can pass quote text.
  // - The image/preview is determined ONLY by the URL's OG meta.
  // - picture= is ignored by modern Facebook sharer.
  const shareToFacebook = () => {
    if (!shareText) {
      setMessage("Nothing to share. Open a post in the modal first.");
      return;
    }

    // If you have a public share page later, put it here.
    // For now, using your app origin (or any public URL) avoids weird localhost scraping issues.
    const shareTargetUrl = window.location.origin;

    const url =
      `https://www.facebook.com/sharer/sharer.php?` +
      `u=${encodeURIComponent(shareTargetUrl)}` +
      `&quote=${encodeURIComponent(shareText)}`;

    window.open(url, "_blank", "width=980,height=720");
  };

  const handlePost = (providerId) => {
    if (providerId === "facebook") return shareToFacebook();
    if (providerId === "linkedin") return shareToLinkedIn();
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <DashboardHeader />

      <main className="max-w-4xl mx-auto w-full px-6 py-10">
        <button
          onClick={() => window.history.back()}
          className="text-sm mb-4 text-gray-600 hover:text-black"
        >
          ← Back
        </button>

        <h1 className="text-3xl font-semibold mb-3">Where would you like to publish to?</h1>

        {message && <div className="mb-4 text-sm text-blue-600">{message}</div>}

        {/* Post preview */}
        <div className="border rounded-2xl p-4 mb-6 bg-white shadow-sm">
          <div className="text-sm font-medium mb-2">Post preview</div>

          <div className="flex gap-4">
            <div className="w-40 h-24 rounded-xl bg-gray-100 overflow-hidden flex items-center justify-center shrink-0">
              {postContent.imageUrl ? (
                <img
                  src={postContent.imageUrl}
                  alt={postContent.title || "Post image"}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-xs text-gray-400">No image</span>
              )}
            </div>

            <div className="min-w-0">
              <div className="font-semibold text-gray-900 line-clamp-2">
                {postContent.title || "Untitled"}
              </div>
              <div className="text-xs text-gray-500 mt-1 line-clamp-4">
                {truncate(stripHtml(postContent.content || ""), 260) || "No content"}
              </div>
            </div>
          </div>

          <div className="mt-3">
            <div className="text-xs text-gray-500 mb-1">Text that will be sent to the platform</div>
            <div
              className="post-content-container"
              dangerouslySetInnerHTML={{ __html: postContent.content }}
            />

            <div className="text-[11px] text-gray-500 mt-2">
              Note: Facebook/LinkedIn previews (image card) require a public URL with OpenGraph tags.
              Localhost cannot generate a preview.
            </div>
          </div>
        </div>

        {/* Providers */}
        <div className="space-y-3">
          {PROVIDERS.map((p) => {
            const acc = connected[p.id];

            return (
              <div
                key={p.id}
                className="flex items-center justify-between border rounded-xl px-5 py-4 bg-white shadow-sm"
              >
                <div>
                  <div className="font-medium">{p.label}</div>
                  <div className="text-xs text-gray-500">{p.description}</div>
                  {acc && (
                    <div className="text-xs text-green-600 mt-1">
                      Connected as {acc.displayName || "Account"}
                    </div>
                  )}
                </div>

                <div className="flex gap-2">
                  {!acc ? (
                    <button
                      className="px-4 py-2 rounded-full text-sm bg-black text-white hover:bg-gray-800"
                      disabled={loading}
                      onClick={() => handleConnect(p.id)}
                    >
                      Connect
                    </button>
                  ) : (
                    <button
                      className="px-4 py-2 rounded-full text-sm bg-gray-100 text-gray-700 hover:bg-gray-200"
                      disabled={loading}
                      onClick={() => handlePost(p.id)}
                    >
                      Post to {p.label}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
}
